import { oc } from "@orpc/contract";
import { z } from "zod";

export const DivisionOptionSchema = z.object({
  code: z.string(),
  label: z.string(),
});

export const GetReportDivisionsResponseSchema = z.object({
  divisions: z.array(DivisionOptionSchema),
});

export const AdminReportType = z.enum(["staged", "staged_internal"]);

/** How the staged / internal-staged reports split their output. */
export const ReportFileGrouping = z.enum(["single", "per_division"]);

export const DownloadTransactionsReportInputSchema = z
  .object({
    reportType: AdminReportType.default("staged"),
    startDate: z.string().date(),
    endDate: z.string().date(),
    divisions: z.array(z.string()).min(1),
    fileGrouping: ReportFileGrouping.default("per_division"),
  })
  .refine(({ startDate, endDate }) => startDate <= endDate, {
    message: "startDate must not be after endDate",
    path: ["endDate"],
  });

const ReportFileSchema = z.object({
  excel: z.string(),
  filename: z.string(),
  divisionCode: z.string(),
  count: z.number(),
});

export const DownloadTransactionsReportResponseSchema = z.object({
  files: z.array(ReportFileSchema),
});

export const DownloadClaimResponseReportInputSchema = z
  .object({
    startDate: z.string().date(),
    endDate: z.string().date(),
    /** Division codes to include. Empty = all divisions. */
    divisions: z.array(z.string()).default([]),
    /** Optional supplier name / trading-partner code (ILIKE match). */
    supplier: z.string().optional(),
    fileGrouping: ReportFileGrouping.default("single"),
  })
  .refine(({ startDate, endDate }) => startDate <= endDate, {
    message: "startDate must not be after endDate",
    path: ["endDate"],
  });

/**
 * GL Posting report — generates the journal-entry workbook (the same one the
 * Staged page produces) for transactions staged on a single day. The `date`
 * matches each row's staged date (when we processed/staged it) — NOT when the
 * source transaction first arrived.
 *
 * Side effect: downloading also POSTS the day's staged rows (status
 * `staged` → `posted`), freezing them so no user can edit, unstage, or
 * mark-as-no-claim them. `sent` and already-`posted` rows are left untouched.
 * Staged rows with no GL configured can't produce a JE line, so they stay
 * staged and are surfaced as warnings (consistent with "Post All Staged").
 */
export const DownloadGlPostingReportInputSchema = z.object({
  date: z.string().date(),
  divisions: z.array(z.string()).min(1),
  /** "single" = one workbook (divisions on separate tabs); "per_division" = one file each. */
  fileGrouping: ReportFileGrouping.default("single"),
});

const GlPostingFileSchema = z.object({
  excel: z.string(),
  filename: z.string(),
  divisionKey: z.string(),
});

const GlPostingWarningSchema = z.object({
  divisionKey: z.string(),
  message: z.string(),
});

export const DownloadGlPostingReportResponseSchema = z.object({
  files: z.array(GlPostingFileSchema),
  warnings: z.array(GlPostingWarningSchema),
  /** Number of staged rows that were posted (frozen) by this download. */
  postedCount: z.number(),
});

export const sdAdminReportsContracts = {
  getDivisions: oc
    .output(GetReportDivisionsResponseSchema)
    .route({ method: "GET" }),
  downloadTransactionsReport: oc
    .input(DownloadTransactionsReportInputSchema)
    .output(DownloadTransactionsReportResponseSchema)
    .route({ method: "POST" }),
  downloadClaimResponseReport: oc
    .input(DownloadClaimResponseReportInputSchema)
    .output(DownloadTransactionsReportResponseSchema)
    .route({ method: "POST" }),
  downloadGlPostingReport: oc
    .input(DownloadGlPostingReportInputSchema)
    .output(DownloadGlPostingReportResponseSchema)
    .route({ method: "POST" }),
} as const;
